// (Copyright) Confluent, Inc.
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import COLORS from '../../constants/colors';
import { SF, INTER } from '../../constants/typefaces';
import { fontBasedStyles } from '../../utils/typefaces';
import { ModalBody } from '../Modal';
import ContentPanel from '../ContentPanel';
import { staticCardStyles } from '../Card';
import { cssVars } from '../../../global';

export const ACTIVE_CLASS = 'ACTIVE_CLASS';

export const activeClassName = 'nav-link-selected';

// Announcement Pages exp. 404, license expiration, ...
export const Centered = styled.div`
  height: var(--MINIMUM_HEIGHT);
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  width: 100%;
`;

export const TopRightWrapper = styled.div`
  height: 32px;
`;
export const TopStaticWrapper = styled.div<any>`
  position: ${({ position }) => position ?? 'fixed'};
  top: 0;
  left: ${({ leftRightOffset }) => `${leftRightOffset ?? 0}px`};
  right: ${({ leftRightOffset }) => `${leftRightOffset ?? 0}px`};
  height: var(--TOPBAR_HEIGHT);
  z-index: var(--TOPBAR_ZINDEX);
`;
type ToolbarProps = {
  $allowFlexWrap?: boolean;
  margin?: string;
};

// takes ToolbarLeft or ToolbarItems as children
export const Toolbar = styled.div<any>`
  display: flex;
  align-items: center;
  margin: ${({ margin }: ToolbarProps) => (margin != null ? margin : '0px')};
  ${({ $allowFlexWrap }: ToolbarProps) => ($allowFlexWrap ? 'flex-wrap: wrap;' : '')}
  pointer-events: none;
`;

Toolbar.displayName = 'Toolbar';

// NOTE: should only contain ToolbarItems that should appear on the left side
export const ToolbarLeft = styled(Toolbar)`
  flex: 1 0 auto;
  margin: 0;
  max-width: 100%;
`;

type ToolbarItemProps = {
  margin?: string;
  width?: string;
};

// NOTE: width can be set in the case that the intended child component
// relies on setting a 100% width
export const ToolbarItem = styled.div<any>`
  pointer-events: all;
  display: flex;
  width: ${({ width }: ToolbarItemProps) => (width != null ? width : '')};
  max-width: 100%;

  * + & {
    margin-left: ${({ margin }: ToolbarItemProps) => (margin != null ? margin : '12px')};
  }
`;

export const Justified = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const JustifiedHeaderWrapper = styled(Justified)`
  margin-bottom: 34px;
`;

export const Sticky = styled.div<any>`
  position: ${({ notSticky }) => (notSticky ? 'relative' : 'sticky')};
  z-index: ${({ zIndex }) => zIndex};
  top: ${({ top }) => top};
  bottom: ${({ bottom }) => bottom};
`;

Sticky.defaultProps = {
  zIndex: 1000,
};

export const LockContainer = styled.div`
  position: absolute;
  right: 15px;
  top: 12px;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const DisabledLockContainer = styled(LockContainer)`
  > svg > g > path {
    fill: var(--DIM_LIGHTER_EVEN);
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const StickyInner = styled.div`
  position: relative;
  display: flex;
`;

/*
Form stuff
*/
export const LargeFieldHeader = styled.div`
  width: 100%;
  margin-bottom: 26px;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0.15px;
  color: var(--DIM-DARKEST);
`;

export const BaseFieldSet = styled.fieldset`
  max-width: var(--FORM_MAX_WIDTH);
  padding: 0;
  align-content: flex-start;
  align-items: flex-start;
  border: none;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
`;
export const FieldSet = styled(BaseFieldSet)<any>`
  margin-bottom: var(--FORM_PADDING_BOTTOM);
  ${({ gap }) => (gap ? `gap: ${gap}px;` : '')};
`;

export const FieldSetSmall = styled(BaseFieldSet)`
  margin-bottom: var(--FORM_PADDING_BOTTOM_SMALL);
`;

export const FieldSetRadio = styled(FieldSet)`
  > div:nth-child(1) {
    margin-bottom: var(--FORM_PADDING_BOTTOM_SMALL);
  }
`;

export const FieldSetCheckbox = styled.div`
  > div:not(:last-child) {
    margin-bottom: var(--FORM_PADDING_BOTTOM_SMALLEST);
  }
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const IndentFieldSet = styled.div`
  border-left: 1px solid var(--DIM_LIGHTER);
  padding-top: 26px;
  padding-left: 18px;
  margin-left: 8px;
  margin-top: 8px;
  max-width: calc(var(--FORM_MAX_WIDTH) - 8px);
  > div {
    margin-bottom: 0px;
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const FieldWrapper = styled.div`
  margin-bottom: var(--FORM_PADDING_BOTTOM);
  flex: 1;
`;

export const FullFieldSet = styled(FieldSet)`
  > div {
    flex-basis: 100%;
  }
`;

export const CompactFieldSet = styled(FieldSet)`
  max-width: var(--FORM_MAX_WIDTH);
  display: flex;
  > div {
    flex: 1;
  }
  > div:nth-child(1) {
    flex-grow: 2;
    padding-right: 16px;
  }
`;

export const CompactFieldSetSmall = styled(FieldSetSmall)`
  max-width: var(--FORM_MAX_WIDTH);
  display: flex;
  > div {
    flex: 1;
  }
  > div:nth-child(1) {
    flex-grow: 2;
    padding-right: 16px;
  }
`;

export const FullField = styled.div`
  flex-basis: 100% !important;
`;

export const Row = styled.div`
  display: flex;
  > * {
    flex: 1;
    &:not(:first-child) {
      margin-left: 32px;
    }
  }
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const SmallSectionHeader = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--DIM_DARKER);
  margin-bottom: 3px;
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const SectionHeader = styled.div`
  border-bottom: 2px solid var(--PRIMARY_LIGHTER_EVEN);

  margin-bottom: 10px;
  padding: 10px 0;
  text-transform: uppercase;

  ${/* sc-selector */ ModalBody} & {
    text-transform: none;
    font-size: 16px;
    font-weight: 300;
    border-bottom: 0;
    margin-bottom: 0;
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const SummaryTable = styled.table<any>`
  width: 100%;
  table-layout: fixed;
  > tbody > tr > td {
    padding: 3px 0px;
    color: var(--DIM);
    white-space: ${({ whiteSpace }) => whiteSpace ?? 'nowrap'};
    font-weight: 300;
  }

  > tbody > tr > td:first-child {
    color: var(--DIM_DARKER);
    vertical-align: top;
    width: ${({ labelCellWidth }) => labelCellWidth ?? '150px'};
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const Section = styled.div`
  margin-bottom: 30px;
`;

export const FormContentPanel = styled(ContentPanel)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-bottom: 0px;
`;

export const FormContentContainer = styled.div`
  display: flex;

  ${FormContentPanel} {
    flex: 1 0 0;
  }
`;

export const ChartHeader = styled.div`
  margin: 0px 14px 24px 0px;
  display: inline-block;
  width: 100%;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const ChartSubheader = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.57;
  color: var(--DIM);
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const legacyLightTextStyles = css`
  font-size: 12px;
  line-height: 20px;
  font-weight: 300;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const TableHeaderLink = styled(Link)`
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--DIM_DARKER);
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const FakeLink = styled.div`
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  text-decoration-line: underline;
  color: var(--PRIMARY_DARKER);
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const NavWrapper = styled.div<{
  disabled?: boolean;
  noBreadcrumbs?: boolean;
  noMinHeight?: boolean;
  width?: string;
}>`
  opacity: ${({ disabled }) => (disabled ? 'var(--UNFOCUSED_OPACITY)' : '')};
  position: sticky;
  top: var(--TOPBAR_HEIGHT);
  min-height: ${({ noMinHeight }) => (noMinHeight ? 'unset' : 'var(--SECONDARY_NAV_MIN_HEIGHT)')};
  height: ${({ noBreadcrumbs }) =>
    noBreadcrumbs ? 'var(--MINIMUM_HEIGHT_CRUMBLESS)' : 'var(--MINIMUM_HEIGHT)'};
  z-index: var(--SIDEBAR_ZINDEX);
  background: var(--secondary_nav_bg);
  flex-shrink: 0;

  ${({ width }) =>
    width
      ? css`
          width: ${width};
        `
      : ''}
`;

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyStrongTextStyles = css`
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.1px;
  color: var(--DIM_DARKEST);
`;
/* eslint-enable rulesdir/no-css-var-colors */

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyTinyTextStyles = css`
  font-weight: 500;
  font-size: 10px;
  line-height: 18px;
  letter-spacing: 0.65px;
  color: var(--DIM_DARKER);
`;
/* eslint-enable rulesdir/no-css-var-colors */

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyH1Styles = css`
  font-weight: 200;
  font-size: 48px;
  line-height: 56px;
  color: var(--DIM_DARKER);
  margin: 0;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 1.15px;`,
    [INTER]: `letter-spacing: -2px;`,
  })}
`;
/* eslint-enable rulesdir/no-css-var-colors */
/**
 * @deprecated use H1 instead
 */
export const LegacyH1 = styled.h1`
  ${legacyH1Styles}
`;

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyH2Styles = css`
  font-weight: bold;
  font-size: 32px;
  line-height: 40px;
  color: var(--DIM_DARKER);
  margin: 0;
  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.15px;`,
    [INTER]: `letter-spacing: -1px;`,
  })}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/**
 * @deprecated use H2 instead
 */
export const LegacyH2 = styled.h2`
  ${legacyH2Styles}
`;

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyH3Styles = css`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: var(--DIM_DARKEST);

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.15px;`,
    [INTER]: `letter-spacing: -1px;`,
  })}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/**
 * @deprecated use H3 instead
 */
export const LegacyH3 = styled.h3`
  ${legacyH3Styles}
`;

/**
 * @deprecated
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const legacyH4Styles = css`
  margin: 0;
  color: var(--DIM_DARKER);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.25px;`,
    [INTER]: `letter-spacing: -0.5px;`,
  })}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/**
 * @deprecated use H4 instead
 */
export const LegacyH4 = styled.h4`
  ${legacyH4Styles}
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const EmptyListPromptSubHeader = styled.h5`
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: var(--DIM_DARKER);
`;
/* eslint-enable rulesdir/no-css-var-colors */

// H5 === Strong (design system)
/**
 * @deprecated use Strong instead
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const LegacyH5 = styled.h5`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: var(--DIM_LIGHTER);

  ${fontBasedStyles({
    [SF]: `letter-spacing: 0.25px;`,
    [INTER]: `letter-spacing: -0.5px;`,
  })}
`;
/* eslint-enable rulesdir/no-css-var-colors */

type Styles = {
  active: boolean;
  disabledStyles: boolean;
  error: boolean;
};

const labelBorderStyle = ({ error, disabledStyles }) => {
  let borderColor = '';
  if (error) {
    // eslint-disable-next-line rulesdir/no-css-var-colors
    borderColor = 'var(--ERROR_DARK)';
  }
  if (!disabledStyles) {
    // eslint-disable-next-line rulesdir/no-css-var-colors
    borderColor = 'var(--PRIMARY_DARKER)';
  }
  return borderColor;
};
/* eslint-disable rulesdir/no-css-var-colors */
const BaseLabelStyle = css<any>`
  background: var(--WHITE);
  border: solid 1px var(--DIM_DARKEST);
  border-radius: 4px;
  transition: all 0.2s;
  border-color: ${({ active, error, disabledStyles }: Styles) =>
    disabledStyles
      ? 'var(--DIM_LIGHTER_EVEN)'
      : error
      ? 'var(--ERROR_DARK)'
      : active
      ? 'var(--PRIMARY_DARKER)'
      : ''};
  cursor: ${({ disabledStyles }) => (disabledStyles ? 'not-allowed !important' : 'default')};
  &:hover {
    border-color: ${({ disabledStyles, error }: Styles) =>
      disabledStyles
        ? 'var(--DIM_LIGHTER_EVEN)'
        : error
        ? 'var(--ERROR_DARK)'
        : 'var(--PRIMARY_DARKER)'};
  }

  ${({ hover, error, disabledStyles }) =>
    hover ? `border-color: ${labelBorderStyle({ error, disabledStyles })};` : ''}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const PrefixWrapper = styled.span`
  ${BaseLabelStyle}
  ${legacyStrongTextStyles}
  line-height: 22px;
  white-space: nowrap;
  border-right: none;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  padding: 13px 0px 13px 16px;
  color: var(--CONTENT_SECONDARY);
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const SuffixWrapper = styled.span`
  ${BaseLabelStyle}
  ${legacyStrongTextStyles}
  line-height: 22px;
  white-space: nowrap;
  border-left: none;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  padding: 13px 16px 13px 0px;
  color: var(--CONTENT_SECONDARY);
`;
/* eslint-enable rulesdir/no-css-var-colors */

const getPadding = ({ hasPrefixText, hasSuffixText }) => {
  if (hasPrefixText) {
    return '13px 16px 13px 0';
  }
  if (hasSuffixText) {
    return '13px 0px 13px 16px';
  }
  return '13px 16px';
};

const getBorder = ({ hasPrefixText, hasSuffixText }) => {
  if (hasPrefixText) {
    return css`
      border-left: none;
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    `;
  }
  if (hasSuffixText) {
    return css`
      border-right: none;
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    `;
  }
  return '';
};
/* eslint-disable rulesdir/no-css-var-colors */
export const LabeledStyle = css<any>`
  ${BaseLabelStyle}
  ${legacyStrongTextStyles}
  ${getBorder}
  padding: ${getPadding};

  width: 100%;
  outline: none;
  color: ${({ disabledStyles }) => (disabledStyles ? 'var(--DIM_LIGHTER)' : 'var(--DIM_DARKEST)')};

  &[type='password'] {
    letter-spacing: 5px;
  }

  &::placeholder {
    opacity: 0;
  }

  &[type='number']::-webkit-inner-spin-button,
  &input[type='number']::-webkit-outer-spin-button {
    appearance: none;
    margin: 0;
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const RoundedCornersPanel = styled.div`
  ${staticCardStyles}
  padding: 16px;
`;

export const RoundedCornersPanelSection = styled.div`
  margin-bottom: 29px;
`;

type CodeBoxProps = {
  fullWidth?: boolean;
  maxHeight?: number | string;
  noBg?: boolean;
};

/* eslint-disable rulesdir/no-css-var-colors */
export const CodeBox = styled.div<any>`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  line-height: 18px;
  color: ${COLORS.PRIMARY_DARKEST};
  position: relative;
  padding: 12px 16px 10px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  max-width: ${({ fullWidth }: CodeBoxProps) => (fullWidth ? '100%' : '870px')};
  height: ${({ maxHeight }: CodeBoxProps) =>
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight ?? 'auto'};
  overflow-y: ${({ maxHeight }: CodeBoxProps) => (maxHeight ? 'scroll' : '')};
  ${({ noBg }: CodeBoxProps) => (noBg ? '' : 'background: var(--DIM_LIGHTEST);')}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const BoxCopyWrapper = styled.div<any>`
  height: ${({ maxHeight }) =>
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight ?? 'auto'};
  position: relative;
  overflow-y: auto;
  border: solid 1px var(--DIM_LIGHTER_EVEN);
  background-color: ${({ backgroundColor }) => backgroundColor ?? 'var(--WHITE)'};
  white-space: pre-wrap;
  word-break: keep-all;
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '870px')};

  transition: all 300ms ease-out;
  ${({ blurred }) => (blurred ? '-webkit-filter: blur(3px);' : '')}
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const StyledHorizontalLine = styled.div<any>`
  height: 2px;
  border-top: solid 1px var(--DIM_LIGHTER_EVEN);
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '870px')};
  top: 43px;
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const CheckboxWrapper = styled.label`
  right: 15px;
  top: 5px;
  display: inline-block;
  text-align: right;
  user-select: none;

  z-index: 2;
  margin-right: 14px;
  > input {
    margin-right: 5px;
  }
`;
export const BoxCopyButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-content: center;
  margin: 12px;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const HorizontalLine = styled.div`
  height: 2px;
  border-top: solid 1px var(--DIM_LIGHTER_EVEN);
  margin-bottom: 11px;
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const SpacerBelow = styled.div`
  padding-bottom: 20px;
  margin-bottom: 20px;
`;
/* eslint-disable rulesdir/no-css-var-colors */
export const CollapsibleSection = styled(SpacerBelow)<any>`
  max-width: 1000px;

  border-bottom: ${(props: any) => (props.divider ? '1px' : '0')} solid var(--DIM_LIGHTER_EVEN);
  ${(props: any) =>
    props.leadDivider
      ? 'padding-top: 20px; margin-top: 27px; border-top: 1px solid var(--DIM_LIGHTER_EVEN);'
      : ''}
  margin-bottom: 27px;

  &:last-of-type,
  > :last-of-type {
    padding-bottom: 0;
    border-bottom: 0;
    margin-bottom: 0;
  }
`;
/* eslint-enable rulesdir/no-css-var-colors */

/* eslint-disable rulesdir/no-css-var-colors */
export const ExpandableTableWrapper = styled.div`
  border-radius: 4px;
  box-shadow: var(--SHADOW_DEFAULT);
  background-color: var(--WHITE);
  padding: 18px 24px;
`;
/* eslint-enable rulesdir/no-css-var-colors */

// Do not use DEPRECATED_wideContent for new UI and try removing it if it is already used.
// Design should design wizard flows fit within 928px.
// If not, either escalate to UIF/design system channels or use SubNavLayout
export const CenteredContentWrapper = styled.div<{ DEPRECATED_wideContent?: boolean }>`
  margin: 0 auto var(--BOTTOMBAR_HEIGHT) auto;
  width: var(--CONTENT_NO_SIDEBAR_WIDTH);

  ${(props) =>
    props.DEPRECATED_wideContent
      ? `
      width: ${cssVars.MAIN_CONTENT_MAX_WIDTH}px;

      // 1400px + 2x32px
      @media (max-width: ${cssVars.MAIN_CONTENT_MAX_WIDTH + 32 * 2}px) {
        & {
          width: 100%;
          margin: 0 32px;
        }
      }
  `
      : `

      @media (max-width: ${cssVars.CONTENT_NO_SIDEBAR_WIDTH + 32 * 2}px) {
        & {
          width: 100%;
          margin: 0 32px;
        }
      }

      `}
`;

/**
 * @deprecated use Tiny instead
 */
/* eslint-disable rulesdir/no-css-var-colors */
export const LegacyTiny = styled.div`
  ${legacyTinyTextStyles};
  color: var(--DIM_LIGHT);
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const AnimatedPanelHeader = styled.h3`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.33;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const WarningText = styled.p`
  margin: 0;
  padding: 10px 16px;
  border-radius: 4px;
  background: var(--SECONDARY_LIGHTEST);
  color: var(--DIM_DARKEST);
`;
/* eslint-enable rulesdir/no-css-var-colors */

export const InfoCardContent = styled.div`
  max-width: var(--FORM_SUPERMAX_WIDTH);
`;

export const InfoCardSectionTitle = styled.div`
  position: relative;
  margin-bottom: 10px;
`;

export const InfoCardSection = styled.div<any>`
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: ${({ isEditable }) =>
    isEditable ? 'var(--SHADOW_DEFAULT)' : 'var(--SHADOW_STATIC)'};
  margin-bottom: 24px;
  opacity: ${({ isActive }: { isActive: boolean }) =>
    isActive !== false ? '' : 'var(--OPACITY_18)'};
  padding: 20px 24px;
`;

export const InfoCardEditButtonWrapper = styled.div`
  margin-top: 10px;
  margin-left: -7px;
`;

/* eslint-disable rulesdir/no-css-var-colors */
export const InfoCardSubheader = styled.div`
  margin-top: 26px;
  color: var(--DIM_DARKER);
  font-weight: 600;
  line-height: 1.5;
`;
/* eslint-enable rulesdir/no-css-var-colors */
export const InfoCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  word-break: break-all;
`;
export const InfoCardGridWithAutoColWidth = styled.div<any>`
  display: grid;
  grid-template-columns: max-content auto;
  grid-column-gap: ${({ gridColumnGap }) => `${gridColumnGap ?? 40}px`};
  word-break: break-all;
`;
export const InfoCardTitleWrapper = styled.div`
  display: inline-block;
`;
export const InfoCardRowTitle = styled.div`
  padding: 4px 0px;
`;
/* eslint-disable rulesdir/no-css-var-colors */
export const InfoCardRowValue = styled.div`
  color: var(--DIM_LIGHT);
`;
/* eslint-enable rulesdir/no-css-var-colors */
// TODO: consolidate these after fixing DS-57
export const ConfigValue = styled.span`
  color: ${COLORS.DIM_LIGHT};
  line-height: 26px;
`;

export const TruncatedText = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
